// @ts-check
/// <reference lib="dom" />
/**
 * Settings Store — one tool's persisted state under one key.
 *
 * **This is the accessor for both `Storage` tiers of the tiers table**
 * (`helpers/storage.js`): `sessionStorage` for per-tab state, `localStorage`
 * for settings, the `Storage` passed in either way. There is no second layer,
 * and a datum's tier is a decision the table states rather than a class to
 * pick.
 *
 * Every rule the tools' own copies had accumulated is kept here: a key per
 * tool and the whole state as one JSON object; a storage that cannot be read
 * or written is a warning, never an error — persistence is a convenience.
 * Nothing here touches a global it was not given beyond the default
 * `localStorage`.
 *
 * **A revision that changes what a stored value MEANS changes the version.**
 * A store may be given one, and it becomes part of the key it reads and writes
 * — `<key>@<version>`. A store given none reads and writes `<key>` exactly as
 * before, which is what every tool does today. `readAnyVersion()` is how a
 * versioned store finds what an earlier one left, including the version-less
 * key: it answers, and migrates nothing, so the caller decides what an old
 * state means now.
 *
 * The control walk — collecting a form's controls into a state and restoring
 * them — is `helpers/settings.js`'s.
 *
 * @module classes/settings/settings-store
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Fable 5.1, Claude Opus 5 (code generation)
 */

/** @typedef {Record<string, unknown>} SettingsState */

/**
 * @typedef {{
 *   key: string,
 *   version: string | null,
 *   state: SettingsState,
 * }} PreviousState
 *   What an earlier revision left. `version` is `null` for the version-less
 *   key — where every tool's state is today — and the version's own label
 *   otherwise. `key` is the literal key it was found under, so a caller that
 *   migrates can clear exactly that and nothing else.
 */

/**
 * Compares two version labels: as numbers when both are whole numbers,
 * otherwise as text. So `10` follows `9` rather than preceding it by its first
 * digit, and a dated label like `2026-09` still orders sensibly against its
 * neighbours.
 *
 * @param {string} one
 * @param {string} other
 */
function compareVersions(one, other) {
    const first = /^\d+$/u.test(one) ? Number(one) : null;
    const second = /^\d+$/u.test(other) ? Number(other) : null;
    if (first !== null && second !== null) return first - second;
    return one < other ? -1 : one > other ? 1 : 0;
}

/**
 * Whether `one` comes after `other`, with the version-less key (`null`) below
 * every version there is: a state written before versions existed is the
 * oldest thing there can be.
 *
 * @param {string | null} one
 * @param {string | null} other
 */
function versionFollows(one, other) {
    if (one === null) return false;
    if (other === null) return true;
    return compareVersions(one, other) > 0;
}

/**
 * One tool's persisted state under one key.
 */
export class SettingsStore {
    /** @type {string} */
    #name;
    /** @type {string | null} */
    #version;
    /** @type {string} */
    #key;
    /** @type {Storage | null | undefined} */
    #storage;
    /** @type {string} */
    #label;
    /** @type {(message: string, error: unknown) => void} */
    #warn;

    /**
     * @param {string} key The tool's key, e.g. `conres-segmenter-state`
     * @param {{
     *   storage?: Storage | null,
     *   version?: string | number | null,
     *   label?: string,
     *   warn?: (message: string, error: unknown) => void,
     * }} [options]
     *   `storage` defaults to `localStorage`; `version`, when given, becomes
     *   part of the stored key (`<key>@<version>`) and a store given none reads
     *   and writes `<key>` unchanged; `label` prefixes warnings; `warn`
     *   receives them (default `console.warn`)
     */
    constructor(key, {
        storage = globalThis.localStorage, version, label = '[Settings]', warn = console.warn,
    } = {}) {
        const versionText = version === undefined || version === null ? null : `${version}`;
        if (versionText !== null && (versionText === '' || versionText.includes('@'))) {
            throw new TypeError(
                `A version is a non-empty label without an “@” — “${versionText}” is not one, so the key `
                + `“${key}@${versionText}” could not be read back as a key and a version again.`);
        }
        this.#name = key;
        this.#version = versionText;
        this.#key = versionText === null ? key : `${key}@${versionText}`;
        this.#storage = storage;
        this.#label = label;
        this.#warn = warn;
    }

    /** The key this store reads and writes — `<key>@<version>` where there is a version. */
    get key() { return this.#key; }

    /** This store's version, or `null` when it was given none. */
    get version() { return this.#version; }

    /**
     * The stored state, or `null` when nothing is stored or it cannot be read.
     *
     * @returns {SettingsState | null}
     */
    read() {
        return this.#readAt(this.#key);
    }

    /**
     * What an EARLIER revision left, when this store's own key holds nothing:
     * the newest other version's state with the version it was written under,
     * or `null`.
     *
     * The version-less key counts as the oldest version there is, and it is the
     * one that matters: every tool stores under a bare key today, so the first
     * store to be given a version finds its predecessor there and nowhere else.
     *
     * **Nothing is migrated.** What an old state means under a new version is
     * the caller's decision, and a layer that guessed it would be deciding for
     * every tool at once. This answers; the caller acts.
     *
     * **The newest other version is the answer, and only it.** When what is
     * stored there is not a state — an array, a string, unparseable text — the
     * answer is `null` rather than the version before it: reaching past a newer
     * key to an older one would hand the caller a state two revisions old while
     * a newer one exists, which is a guess of exactly the kind this method
     * refuses to make.
     *
     * A `Storage` that cannot be listed — one answering `getItem`, `setItem`
     * and `removeItem` but neither `length` nor `key(index)`, which is all this
     * store has ever needed — is a warning and a `null`, because answering
     * `null` silently would say "nothing to migrate" when the truth is that the
     * question could not be asked.
     *
     * @returns {PreviousState | null}
     */
    readAnyVersion() {
        const storage = this.#storage;
        if (!storage) return null;
        // The current version answers through `read()`. Looking past it while
        // it holds something would offer a caller a migration it already made.
        if (this.read() !== null) return null;

        // Reading `length` is itself a call that can refuse — a profile with
        // site data blocked throws on the property, not only on `getItem` — so
        // the capability probe is inside the guard as much as the walk is.
        /** @type {boolean} */
        let listable;
        try {
            listable = typeof storage.key === 'function' && typeof storage.length === 'number';
        } catch (error) {
            this.#warn(
                `${this.#label} Could not list the storage for an earlier version of “${this.#name}”:`, error);
            return null;
        }
        if (!listable) {
            this.#warn(
                `${this.#label} Could not look for an earlier version of “${this.#name}” — this Storage `
                + 'cannot be listed:',
                new TypeError('The Storage answers neither length nor key(index).'));
            return null;
        }

        /** @type {{ key: string, version: string | null } | null} */
        let newest = null;
        try {
            for (let index = 0; index < storage.length; index++) {
                const key = storage.key(index);
                if (key === null) continue;
                const version = this.#versionOf(key);
                // Not this store's key at all, or its own — which is empty, or
                // `read()` would have answered above.
                if (version === undefined || version === this.#version) continue;
                if (newest === null || versionFollows(version, newest.version)) newest = { key, version };
            }
        } catch (error) {
            this.#warn(
                `${this.#label} Could not list the storage for an earlier version of “${this.#name}”:`, error);
            return null;
        }
        if (newest === null) return null;

        const state = this.#readAt(newest.key);
        return state === null ? null : { key: newest.key, version: newest.version, state };
    }

    /**
     * Replaces the stored state.
     *
     * @param {SettingsState} state
     */
    write(state) {
        try {
            this.#storage?.setItem(this.#key, JSON.stringify(state));
        } catch (error) {
            this.#warn(`${this.#label} Could not persist state (storage full or unavailable):`, error);
        }
    }

    /**
     * Reads, lets `mutate` change the state in place, writes — the shape of
     * every `#persistState` there was. Starts from `{}` when nothing is stored.
     *
     * @param {(state: SettingsState) => void} mutate
     * @returns {SettingsState} the state as written
     */
    update(mutate) {
        const state = this.read() ?? {};
        mutate(state);
        this.write(state);
        return state;
    }

    /** Removes the stored state. */
    clear() {
        try {
            this.#storage?.removeItem(this.#key);
        } catch (error) {
            this.#warn(`${this.#label} Could not clear persisted state:`, error);
        }
    }

    /**
     * Removes keys an earlier revision wrote outside the store — dead weight
     * nothing reads.
     *
     * @param {Iterable<string>} keys literal storage keys
     */
    removeLegacyKeys(keys) {
        try {
            for (const key of keys) this.#storage?.removeItem(key);
        } catch (error) {
            this.#warn(`${this.#label} Could not clear the legacy state keys:`, error);
        }
    }

    /**
     * The version a key of THIS store carries: `null` for the version-less key,
     * the label for `<key>@<label>`, and `undefined` for a key that is not this
     * store's at all — another tool's, and equally a key that merely begins the
     * same way (`<key>-extra` is a different tool, not a version of this one).
     *
     * @param {string} key
     * @returns {string | null | undefined}
     */
    #versionOf(key) {
        if (key === this.#name) return null;
        const prefix = `${this.#name}@`;
        if (!key.startsWith(prefix)) return undefined;
        const version = key.slice(prefix.length);
        return version === '' ? undefined : version;
    }

    /**
     * One key's state, or `null` when nothing is stored there, it cannot be
     * read, or what is there is not a JSON object — an array or a string is
     * not a state, and handing one back would give a caller something it
     * cannot restore.
     *
     * @param {string} key
     * @returns {SettingsState | null}
     */
    #readAt(key) {
        try {
            const raw = this.#storage?.getItem(key);
            const parsed = raw ? JSON.parse(raw) : null;
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
        } catch (error) {
            this.#warn(`${this.#label} Could not read persisted state — starting from the HTML defaults:`, error);
            return null;
        }
    }
}
